import { m, sentences, type Message } from '@/i18n';
import { meansApplyPlan } from '../flow-definition';
import type { MeansPair } from '../stage-gates';
import { domainError, type Command, type DomainError } from '../types';
import { planFrom } from './plan';
import { FORCE_ANSWER, LEAVE_ANSWER, SWEEP_ANSWER } from './policies';
import { dispatchedCommands } from './shared';
import { questionId } from './stimulus';
import {
  PLAN_MAX_ITEMS,
  type Answer,
  type ChildContext,
  type Context,
  type Intent,
  type Question,
  type QuestionClass,
  type QuestionOption,
  type QuestionSubject,
  type Resolution,
  type Stimulus,
} from './types';

/**
 * **Step 3: clarification** (docs/RESOLUTION.md §1, §3, §6). A typed `Question`
 * where stimulus plus context admit more than one meaning, and how that
 * question is answered per mode: a value for interactive and agentic, the
 * rule's own pre-written policy for async, never a human in the loop per
 * firing.
 *
 * §6's conclusion question is the one question the platform asks today, so the
 * sets it offers ("close them too?") live beside it.
 */

/**
 * **The three presets: leave, the safe sweep, and force** (ruled by Leon
 * 2026-09-22). Each is a NAME for a set of open descendants, and the answer
 * carries the SET - so a person may take a preset and then tick boxes on and
 * off, while the pure layer keeps one code path: a selection in, a cascade
 * out. A selection matching no preset is simply custom; nobody picks it, they
 * arrive at it.
 *
 * **Force overrides TIES, never AUTHORITY or SIGHT.** A child another open
 * list holds, and a child open work outside this item depends on, are judgment
 * calls that belong to the person concluding their own work. A child they do
 * not hold the seat for is someone else's act; a child they cannot see cannot
 * be acted on at all. Those two stay out of every preset, and a selection that
 * names one refuses the whole gesture rather than partially applying it.
 *
 * There is still no done/cancelled choice: a swept child takes its PARENT's
 * reading (Leon, 2026-09-28: "read as one", `cascadeFor`). A parent that ends
 * done delivers and closes what it sweeps; a parent that ends cancelled only
 * closes it, so an undelivered child reads cancelled, loud to its own parties
 * as the warning-unless-solo law says.
 */

/** The key a selection that matches no preset carries. Never a rendered row:
 *  ticking a box is the only way into it. */
export const CUSTOM_ANSWER = 'custom';

/** The open subtree as §6 reads it: the three sets, and what is out of sight. */
export interface Conclusion {
  /** Every open descendant the viewer can SEE, as the composer lists them. */
  children: readonly ChildContext[];
  /** The SAFE preset: what is the person's alone, deepest first. */
  safe: readonly ChildContext[];
  /** The FORCE preset: the safe set plus what is tied to open work outside
   *  this item, deepest first. A superset of `safe` by construction. */
  forced: readonly ChildContext[];
  /** What force takes and the safe sweep leaves. Empty = no third answer. */
  tied: readonly ChildContext[];
  /** Open descendants out of sight: counted, never named, never planned. */
  hidden: number;
  /** Every open descendant this conclusion weighed, seen or not. */
  total: number;
}

/** Whether open work OUTSIDE the concluded set is tied to this child: another
 *  open parent lists it, or an open item depends on it. */
function isTied(child: ChildContext): boolean {
  return child.outsideParents > 0 || child.outsideDependents > 0;
}

/**
 * The set that closes under one seed, run to a fixpoint.
 *
 * **A skip travels DOWNWARD**: a child that stays open keeps its own work, so
 * a descendant reachable only through a staying item stays too. Hierarchy is a
 * DAG and `depth` is not a topological order (two siblings may list each
 * other), so this runs to a fixpoint rather than in one pass. The fixpoint is
 * monotone in its seed, which is why force's set contains the safe one.
 *
 * A parent the actor sees only as its stub is never in the set (it is not one
 * of the `children`), so a child under one stays, like a child under any
 * other parent that stays.
 */
function closingSet(
  children: readonly ChildContext[],
  targetId: string,
  seed: (child: ChildContext) => boolean,
): Set<string> {
  const set = new Set(children.filter(seed).map((c) => c.itemId));
  for (let round = 0; round <= children.length; round += 1) {
    let moved = false;
    for (const c of children) {
      if (!set.has(c.itemId)) continue;
      if (
        c.stubParentsInSubtree.length === 0 &&
        c.parentsInSubtree.every((p) => p === targetId || set.has(p))
      ) {
        continue;
      }
      set.delete(c.itemId);
      moved = true;
    }
    if (!moved) break;
  }
  return set;
}

/** Deepest first, so a parent's own children are concluded before it is. */
function deepestFirst(children: readonly ChildContext[]): ChildContext[] {
  return [...children].sort((a, b) => b.depth - a.depth);
}

/** The three sets, computed once for a gesture (§6). */
export function conclusionOf(
  children: readonly ChildContext[],
  hidden: number,
  targetId: string,
): Conclusion {
  const safeIds = closingSet(children, targetId, (c) => c.canAct && !isTied(c));
  const forcedIds = closingSet(children, targetId, (c) => c.canAct);
  return {
    children,
    safe: deepestFirst(children.filter((c) => safeIds.has(c.itemId))),
    forced: deepestFirst(children.filter((c) => forcedIds.has(c.itemId))),
    tied: deepestFirst(children.filter((c) => forcedIds.has(c.itemId) && !safeIds.has(c.itemId))),
    hidden,
    total: children.length + hidden,
  };
}

/** The parent's reading after its OWN plan, which its swept children take. */
export interface ParentReading {
  delivered: boolean;
}

/** The pairs a swept child takes from its parent's reading: delivered and
 *  closed under a parent that ends done, closed alone under one that ends
 *  cancelled. */
const SWEEP_DONE: readonly MeansPair[] = [
  { attr: 'delivered', value: true },
  { attr: 'closed', value: true },
];
const SWEEP_CANCELLED: readonly MeansPair[] = [{ attr: 'closed', value: true }];

/**
 * The commands one child of a cascade carries, compiled through the one act
 * compiler like every other pair set.
 *
 * **A swept child takes its parent's reading** (Leon, 2026-09-28: "read as
 * one"). A parent that ends DELIVERED and closed reads done, so each child it
 * sweeps is delivered and closed too; a parent that ends closed without a
 * delivery reads cancelled, so each child is only closed. The compiler emits
 * `Tender` only where the child is undelivered, so a delivered child gets
 * `Close` alone either way. Until this ruling the cascade applied `Close`
 * alone (2026-09-22), and ticking a category done left its undelivered lines
 * reading cancelled: the ✕ on the rail under a ticked head.
 */
export function cascadeFor(
  selected: readonly ChildContext[],
  reading: ParentReading,
): Intent['cascade'] {
  const pairs = reading.delivered ? SWEEP_DONE : SWEEP_CANCELLED;
  return selected.map((child) => ({
    itemId: child.itemId,
    commands: dispatchedCommands(meansApplyPlan(pairs, child.state, 'flow:self')),
  }));
}

/**
 * Whether the target ends DELIVERED after its own commands: the delivery it
 * starts with, then each `Tender` and `Retract` in order. A cancellation
 * compiles a `Retract` where a delivery stands and never a `Tender`, so a
 * cancel never delivers.
 */
export function deliveredAfter(delivered: boolean, commands: readonly Command[]): boolean {
  let out = delivered;
  for (const command of commands) {
    if (command.type === 'Tender') out = true;
    else if (command.type === 'Retract') out = false;
  }
  return out;
}

/** "3 sub-tasks stay open." - what a conclusion leaves standing. Spoken by the
 *  question's answers and by a tick, which closes without asking (§2) and
 *  says the same thing about what it leaves. */
export function stayClause(n: number): Message {
  return m('resolution.consequence.subTasksStayOpen', { count: n });
}

/** "Also finishes 3 sub-items." / "Also cancels 3 sub-items." - what a
 *  sweeping answer takes with it, in the reading its children take. */
export function alsoClosesClause(n: number, done: boolean): Message {
  return m(
    done
      ? 'resolution.consequence.alsoFinishesSubTasks'
      : 'resolution.consequence.alsoCancelsSubTasks',
    { count: n },
  );
}

/** The consequences of a conclusion that swept part of its subtree. `done`:
 *  the parent ends done, so what it takes reads done too. */
export function sweptConsequences(closed: number, staying: number, done = true): Message[] {
  const out: Message[] = [];
  if (closed > 0) out.push(alsoClosesClause(closed, done));
  if (staying > 0) out.push(stayClause(staying));
  return out;
}

// --- What a selection leaves standing, and the words for it ------------------

/** The three facts that keep a child out of a selection, kept apart because
 *  they are different facts and force overrides only the first. */
interface Stays {
  /** Open work outside this item still needs them. */
  tied: string[];
  /** Not the actor's to conclude: no seat, or out of sight (`hidden`). */
  locked: string[];
  /** Only reachable through a parent that itself stays open. */
  held: string[];
  hidden: number;
}

function staysOf(conclusion: Conclusion, selection: ReadonlySet<string>): Stays {
  const stays: Stays = { tied: [], locked: [], held: [], hidden: conclusion.hidden };
  for (const child of conclusion.children) {
    if (selection.has(child.itemId)) continue;
    if (!child.canAct) stays.locked.push(child.state.title);
    else if (isTied(child)) stays.tied.push(child.state.title);
    else stays.held.push(child.state.title);
  }
  return stays;
}

/**
 * **Named where the viewer can see them, counted where they cannot** (Leon,
 * 2026-09-22: the answer must show what it is protecting). A child outside the
 * actor's sight is never named, so any group carrying one is counted whole;
 * more than two are counted rather than listed. One sentence per reason a
 * child stays out of a selection.
 */
type StaysKey = 'resolution.stays.tied' | 'resolution.stays.locked' | 'resolution.stays.held';

function staysClause(titles: readonly string[], hidden: number, key: StaysKey): Message | null {
  const n = titles.length + hidden;
  if (n === 0) return null;
  const subject =
    hidden === 0 && titles.length <= 2 ? titles : m('resolution.subject.subTasks', { count: n });
  return m(key, { subject, count: n });
}

/** The sub-line of an answer: what stays and why, one sentence per fact. */
function staysDetail(stays: Stays): Message | undefined {
  return sentences(
    [
      staysClause(stays.tied, 0, 'resolution.stays.tied'),
      staysClause(stays.locked, stays.hidden, 'resolution.stays.locked'),
      staysClause(stays.held, 0, 'resolution.stays.held'),
    ].filter((clause): clause is Message => clause !== null),
  );
}

/** The extra force takes, named where it can be: the whole point of the third
 *  answer is that the person sees what the safe one was protecting. */
function forceTakesClause(tied: readonly ChildContext[], alongside: boolean): Message {
  const titles = tied.map((c) => c.state.title);
  if (titles.length <= 2) {
    return m(alongside ? 'resolution.force.alsoClosesNamed' : 'resolution.force.closesNamed', {
      titles,
    });
  }
  return m(alongside ? 'resolution.force.alsoClosesMore' : 'resolution.force.closesMore', {
    count: titles.length,
  });
}

// --- The answers, and the list they are presets over -------------------------

function withDetail(option: QuestionOption, detail: Message | undefined): QuestionOption {
  return detail ? { ...option, detail } : option;
}

function idsOf(children: readonly ChildContext[]): string[] {
  return children.map((c) => c.itemId);
}

/**
 * The answers this conclusion offers. **A row that would do exactly what
 * another row does must not render** (Leon, 2026-09-22): the safe sweep
 * appears only where it takes something, and force only where the safe sweep
 * skips a child for a TIE - never for authority or sight, which force does not
 * override.
 */
function answersFor(conclusion: Conclusion, open: number, done: boolean): QuestionOption[] {
  const too = done ? 'resolution.answer.finishToo' : 'resolution.answer.cancelToo';
  const options: QuestionOption[] = [];
  const sweeps = conclusion.safe.length > 0;
  if (sweeps) {
    options.push(
      withDetail(
        {
          key: SWEEP_ANSWER,
          label: m(too, { count: open }),
          selection: idsOf(conclusion.safe),
        },
        staysDetail(staysOf(conclusion, new Set(idsOf(conclusion.safe)))),
      ),
    );
  }
  if (conclusion.tied.length > 0) {
    const takes = forceTakesClause(conclusion.tied, sweeps);
    const stays = staysDetail(staysOf(conclusion, new Set(idsOf(conclusion.forced))));
    options.push(
      withDetail(
        {
          key: FORCE_ANSWER,
          // "All" was the misleading word (Leon, 2026-09-22): the row does not
          // close all of them, it stops protecting the ties - which is why its
          // sub-line names them.
          label: sweeps
            ? m(done ? 'resolution.answer.finishAll' : 'resolution.answer.cancelAll')
            : m(too, { count: open }),
          selection: idsOf(conclusion.forced),
          danger: true,
        },
        sentences(stays ? [takes, stays] : [takes]),
      ),
    );
  }
  options.push({
    key: LEAVE_ANSWER,
    // Its label is the whole act, so it carries no sub-line
    // (docs/DESIGN_SYSTEM.md: only where the act costs more than its word says).
    label: m('resolution.answer.leaveOpen', { count: open }),
    selection: [],
  });
  return options;
}

/** The two answers a surface that cannot sweep still speaks (a board lane says
 *  the sentence and reverts): no sets, so an answer that would sweep is
 *  refused rather than guessed at. */
function ungatheredAnswers(open: number, done: boolean): QuestionOption[] {
  return [
    {
      key: SWEEP_ANSWER,
      label: m(done ? 'resolution.answer.finishToo' : 'resolution.answer.cancelToo', {
        count: open,
      }),
    },
    { key: LEAVE_ANSWER, label: m('resolution.answer.leaveOpen', { count: open }) },
  ];
}

/** What the composer lists: every open descendant the viewer can see, with the
 *  seat word where it is not theirs and the tie where the safe preset leaves
 *  it. Shallowest first, so the list reads as the tree it is. */
function subjectsOf(conclusion: Conclusion): QuestionSubject[] {
  return (
    [...conclusion.children]
      // Shallowest first, then by title, so the same subtree always lists in the
      // same order - a checkbox that moves between renders is not tickable.
      .sort((a, b) => a.depth - b.depth || a.state.title.localeCompare(b.state.title))
      .map((child) => ({
        itemId: child.itemId,
        title: child.state.title,
        depth: child.depth,
        selectable: child.canAct,
        // The SEAT, never a person (docs/DESIGN_SYSTEM.md).
        ...(child.canAct ? {} : { lock: m('resolution.subject.requesterOnly') }),
        ...(child.outsideParents > 0
          ? { tie: 'listed' as const }
          : child.outsideDependents > 0
            ? { tie: 'needed' as const }
            : {}),
      }))
  );
}

// --- The guard ---------------------------------------------------------------

/**
 * **The conclusion guard is a QUESTION, not a wall** (re-cut by Leon
 * 2026-09-21; a third preset and a per-child list, 2026-09-22). A parent's
 * owner can always conclude their own item: the item closes when every real
 * child is concluded, or when the person answered the question. A closed
 * parent may list an open child - the edge stays, nothing is released and
 * nothing below is overridden.
 *
 * One line - *"3 sub-items are still open. Cancel them too?"*, or *Finish
 * them too?* where the parent ends done - over three
 * presets and the children themselves, each answer saying in its own sub-line
 * what it leaves and why. Where nothing below is the actor's to take there is
 * nothing to ask, and the item closes with the clause as its consequence.
 */
export function planWithConclusionGuard(
  stimulus: Stimulus,
  context: Context,
  intent: Intent,
): Resolution {
  if (context.item.closed || context.weight.openChildren === 0) {
    return planFrom(stimulus, context, intent);
  }
  // Without the subtree the guard can still ASK (a board lane speaks the
  // sentence and reverts); it cannot sweep, and says so if an answer arrives.
  const conclusion = context.children
    ? conclusionOf(context.children, context.hiddenChildren ?? 0, stimulus.itemId)
    : null;

  // Nothing below is the actor's to take under either preset: nothing to ask.
  if (conclusion && conclusion.forced.length === 0) {
    return planFrom(stimulus, context, intent, { consequences: [stayClause(conclusion.total)] });
  }

  const open = conclusion ? conclusion.total : context.weight.openChildren;
  // The children take the parent's reading after ITS plan (2026-09-28): a
  // close over a standing delivery reads done, a cancel never delivers - and
  // the question says which it is (2026-11-03).
  const reading = { delivered: deliveredAfter(context.item.delivered, intent.commands) };
  const klass: QuestionClass = 'open-children';
  const question: Question = {
    id: questionId(stimulus.key, klass),
    // It names what will happen to the open work, not which meaning the
    // gesture had (§1 step 6: "this is what will happen, yes?").
    kind: 'confirmation',
    class: klass,
    text: m(
      reading.delivered
        ? 'resolution.question.subTasksOpen.done'
        : 'resolution.question.subTasksOpen.cancel',
      { count: open },
    ),
    options: conclusion
      ? answersFor(conclusion, open, reading.delivered)
      : ungatheredAnswers(open, reading.delivered),
    answerers: [...context.item.requesters],
    ...(conclusion ? { subjects: subjectsOf(conclusion) } : {}),
  };
  const answered = answerFor(question, context);
  if (answered.kind === 'unanswered') return answered.resolution(intent);

  const chosen = selectionFor(question, conclusion, answered.answer);
  if (!chosen.ok) return { kind: 'refused', intent, error: chosen.error };

  if (chosen.ids.length === 0) {
    return planFrom(stimulus, context, intent, { consequences: [stayClause(open)] });
  }
  if (chosen.ids.length + 1 > PLAN_MAX_ITEMS) {
    // class: 'cascade-size' - absolute, so no policy answers it (§6).
    return {
      kind: 'refused',
      intent,
      error: domainError(
        'invalid_input',
        m('resolution.refusal.tooLarge', { count: chosen.ids.length + 1 }),
      ),
    };
  }

  const taken = new Set(chosen.ids);
  const selected = deepestFirst((conclusion?.children ?? []).filter((c) => taken.has(c.itemId)));
  return planFrom(
    stimulus,
    context,
    { ...intent, cascade: cascadeFor(selected, reading) },
    { consequences: sweptConsequences(selected.length, open - selected.length, reading.delivered) },
  );
}

type Chosen = { ok: true; ids: readonly string[] } | { ok: false; error: DomainError };

const NOT_GATHERED: DomainError = domainError('invalid_state', m('resolution.refusal.notGathered'));

/**
 * **A selection never widens what the machine permits** (§6). An explicit set
 * is checked against the subtree the resolver itself gathered: every id must
 * be an open descendant the viewer can SEE and holds the seat to conclude.
 * Anything else refuses the whole gesture rather than partially applying it -
 * `decide` and the write gate still rule, and this is the layer that must not
 * hand them a step nobody may take.
 *
 * With no explicit set the option's own preset answers, which is how a policy
 * (`{ answer: 'sweep' }`) and an agent naming a preset by word reach the same
 * sets a person ticks.
 */
function selectionFor(question: Question, conclusion: Conclusion | null, answer: Answer): Chosen {
  if (answer.selection) {
    if (!conclusion) return { ok: false, error: NOT_GATHERED };
    const selectable = new Map(conclusion.children.map((c) => [c.itemId, c.canAct]));
    for (const id of answer.selection) {
      if (selectable.get(id) !== true) {
        return {
          ok: false,
          error: domainError('forbidden', m('resolution.refusal.notYoursToConclude')),
        };
      }
    }
    return { ok: true, ids: [...new Set(answer.selection)] };
  }

  const option = question.options.find((o) => o.key === answer.optionKey);
  if (!option) {
    return {
      ok: false,
      error: domainError(
        'invalid_input',
        m('resolution.refusal.notAnAnswer', { answer: answer.optionKey }),
      ),
    };
  }
  if (option.selection) return { ok: true, ids: option.selection };
  // The ungathered question: "leave" still means leave; anything that would
  // sweep has no set to sweep.
  return option.key === LEAVE_ANSWER ? { ok: true, ids: [] } : { ok: false, error: NOT_GATHERED };
}

type AnswerLookup =
  | { kind: 'answered'; answer: Answer }
  | { kind: 'unanswered'; resolution: (intent: Intent) => Resolution };

/**
 * §3/§4: how a question is answered, per mode. Interactive and agentic return
 * the question as a value (a surface renders it, an agent reads it); async
 * never waits and consults the rule's POLICY for this class - `answer`
 * continues, `propose` raises the intent as a proposal, `skip` (or no policy
 * at all) is a finding that tells the author their rule hit a question it
 * never anticipated. A question with nobody to ask is the vacancy fact: the
 * plan is refused whole, whatever the mode.
 */
export function answerFor(question: Question, context: Context): AnswerLookup {
  const given = context.answers.find((a) => a.questionId === question.id);
  if (given) return { kind: 'answered', answer: given };

  if (question.answerers.length === 0) {
    return {
      kind: 'unanswered',
      resolution: (intent) => ({
        kind: 'refused',
        intent,
        questionClass: question.class,
        error: domainError(
          'invalid_state',
          m('resolution.refusal.nobodyToAnswer', { questionClass: question.class }),
        ),
      }),
    };
  }

  if (context.mode !== 'async') {
    return { kind: 'unanswered', resolution: () => ({ kind: 'question', question }) };
  }

  const policy = context.policies?.[question.class];
  if (policy && typeof policy === 'object') {
    return { kind: 'answered', answer: { questionId: question.id, optionKey: policy.answer } };
  }
  if (policy === 'propose') {
    return {
      kind: 'unanswered',
      resolution: (intent) => ({
        kind: 'lane',
        lane: 'propose',
        intent,
        reason: m('resolution.lane.ruleNeedsAnswer', { questionClass: question.class }),
        questionClass: question.class,
      }),
    };
  }
  return {
    kind: 'unanswered',
    // `skip`, and no policy at all: the caller appends this as a `flow_finding`
    // (docs/FLOW_DEFINITION.md §8b) - the signal to amend the rule, never a
    // human in the loop per firing.
    resolution: (intent) => ({
      kind: 'refused',
      intent,
      questionClass: question.class,
      error: domainError(
        'invalid_state',
        m(policy === 'skip' ? 'resolution.refusal.ruleSkips' : 'resolution.refusal.noPolicy', {
          questionClass: question.class,
        }),
      ),
    }),
  };
}
